import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "./BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import ContratLocationSheet, { SHEET_WIDTH } from "@components/contrat/ContratLocationSheet";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import { missingContratFields, type ContratFields } from "types/contrat_location";
import type { Ordre } from "types/ordre";
import type { Vehicle } from "types/garage/vehicules";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import type { Org } from "types/org";

interface ContratLocationModalProps {
  ordre: Ordre;
  rentalVehicle: Vehicle;
  contact?: Contact | null;
  clientVehicle?: ClientVehicle | null;
  orgData: Org;
  onClose: () => void;
}

/**
 * Contrat de location / prêt: the contract is shown as an editable sheet (every printed
 * value is an input), then generated as a one-page PDF with exactly what is on screen.
 */
export default function ContratLocationModal({ ordre, orgData, onClose }: ContratLocationModalProps) {
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<ContratFields>({});
  const [usage, setUsage] = useState<"location" | "pret">(
    ordre.mobility_option === "location" || ordre.mobility_option === "rental" ? "location" : "pret",
  );
  const [conditionsUrl, setConditionsUrl] = useState("");
  const [conditionsQr, setConditionsQr] = useState("");
  const [org, setOrg] = useState<Record<string, string>>(
    (orgData.infos as unknown as Record<string, string>) || {},
  );

  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [edlUrl, setEdlUrl] = useState<string | null>(null);
  const [tab, setTab] = useState<"edit" | "contrat" | "edl">("edit");

  const usageLabel = usage === "location" ? "Location" : "Prêt";

  // ── Load the pre-filled fields (same defaults the PDF uses) ──────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ordresApi.getContratLocationFields(ordre.id);
        if (cancelled) return;
        setFields(data.fields || {});
        setUsage(data.usage === "location" ? "location" : "pret");
        setConditionsUrl(data.conditions_url || "");
        setConditionsQr(data.conditions_qr || "");
        if (data.org) setOrg(data.org);
      } catch {
        if (!cancelled) showNotification({ type: "error", message: "Impossible de charger le contrat" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ordre.id]);

  const setField = useCallback((key: string, value: string | boolean) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Scale the A4 sheet down to the modal width ──────────────────────────────
  const wrapRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState(0);

  useLayoutEffect(() => {
    const update = () => {
      const available = wrapRef.current?.clientWidth || SHEET_WIDTH;
      const next = Math.min(1, available / SHEET_WIDTH);
      setScale(next);
      setScaledHeight((sheetRef.current?.offsetHeight || 0) * next);
    };
    update();
    const observer = new ResizeObserver(update);
    if (wrapRef.current) observer.observe(wrapRef.current);
    if (sheetRef.current) observer.observe(sheetRef.current);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [loading, tab]);

  // The licence details have to be on the contract: no generation while they are empty.
  const missing = missingContratFields(fields);
  const missingKeys = missing.map((f) => f.key);

  const handleGenerate = async () => {
    if (missing.length) {
      setTab("edit");
      showNotification({
        type: "error",
        message: `Champs obligatoires manquants : ${missing.map((f) => f.label).join(", ")}`,
      });
      return;
    }
    setGenerating(true);
    try {
      const result = await ordresApi.generateContratLocation(ordre.id, fields);
      if (result.fields) setFields(result.fields);
      const docs = result.documents || [];
      setPdfUrl(docs[0]?.url || null);
      setEdlUrl(docs[1]?.url || null);
      if (docs.length) setTab("contrat");
      const updatedOrdre = await ordresApi.get(ordre.id);
      useOrdreStore.getState().updateOrdre(updatedOrdre);
      showNotification({ type: "success", message: "Documents générés" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la génération" });
    } finally {
      setGenerating(false);
    }
  };

  const tabButton = (key: "edit" | "contrat" | "edl", label: string) => (
    <button
      className={`px-3 py-1.5 rounded-md text-h3 font-medium transition-colors ${
        tab === key ? "bg-white shadow-sm text-black" : "text-neutral-400 hover:text-black"
      }`}
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  );

  return (
    <BaseModal
      title={`Contrat de ${usageLabel}`}
      startIcon={<Icon1 name="document" size={20} />}
      onClose={onClose}
      width="max-w-5xl"
    >
      <div className="flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Toolbar */}
        <div className="flex flex-row items-center justify-between gap-2 p-2 border-b border-neutral-200 bg-neutral-50">
          <div className="flex flex-row gap-1">
            {tabButton("edit", "Contrat (éditable)")}
            {pdfUrl && tabButton("contrat", "PDF généré")}
            {edlUrl && tabButton("edl", "État des lieux")}
          </div>
          <div className="flex flex-row items-center gap-2">
            {tab === "edit" && (
              <span className={`text-h4 hidden md:block ${missing.length && !loading ? "text-red-600 font-medium" : "text-neutral-400"}`}>
                {missing.length && !loading
                  ? `Permis du conducteur à renseigner (${missing.length} champ${missing.length > 1 ? "s" : ""})`
                  : "Cliquez sur n'importe quel champ pour le remplir — 1 page garantie"}
              </span>
            )}
            {pdfUrl ? (
              <SecondaryButton
                label="Re-générer"
                onClick={handleGenerate}
                disabled={generating || loading || missing.length > 0}
              />
            ) : (
              <Button
                label={generating ? "Génération..." : "Générer les documents"}
                onClick={handleGenerate}
                disabled={generating || loading || missing.length > 0}
                loading={generating}
                variant="small"
              />
            )}
          </div>
        </div>

        {/* Editable sheet */}
        {tab === "edit" && (
          <div className="overflow-y-auto bg-neutral-100 p-4" style={{ minHeight: "60vh" }}>
            {loading ? (
              <div className="text-h3 text-neutral-400 text-center py-10">Chargement du contrat...</div>
            ) : (
              <div ref={wrapRef} className="w-full">
                <div style={{ height: scaledHeight || undefined }}>
                  <div
                    ref={sheetRef}
                    style={{
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                      width: SHEET_WIDTH,
                      boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
                    }}
                  >
                    <ContratLocationSheet
                      fields={fields}
                      onChange={setField}
                      usage={usage}
                      org={org}
                      conditionsUrl={conditionsUrl}
                      conditionsQr={conditionsQr}
                      missingKeys={missingKeys}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Generated PDFs */}
        {tab === "contrat" && pdfUrl && (
          <div className="flex-1" style={{ minHeight: "70vh" }}>
            <iframe src={pdfUrl} className="w-full h-full border-0" style={{ minHeight: "70vh" }} title="Contrat de location" />
          </div>
        )}
        {tab === "edl" && edlUrl && (
          <div className="flex-1" style={{ minHeight: "70vh" }}>
            <iframe src={edlUrl} className="w-full h-full border-0" style={{ minHeight: "70vh" }} title="État des lieux" />
          </div>
        )}
      </div>
    </BaseModal>
  );
}
